import React from 'react'
import { Link } from 'react-router'
import { useList, useUpdate } from '@refinedev/core'
import { App, Button, Switch, Tooltip } from 'antd'
import { brand } from '../../theme/brand'
import { ListPage, Pill, SectionCard, type Stat } from '../../theme/listkit'
import { UsersRound, GraduationCap, Rocket, ShieldCheck, Bell } from '../../components/icons'
import { GroupsAudienceCard } from './GroupsAudienceCard'
import { useClassroomSettings, type ClassroomSettings } from '../group_chapters/useClassroomSettings'
import { classesPhrase, normalizeYears } from '../group_chapters/rollout'

interface FeatureFlag {
  key: string
  enabled: boolean
  description: string | null
  updated_at: string
}

const muted: React.CSSProperties = { fontFamily: brand.fSans, fontWeight: 500, fontSize: 12, color: brand.sub, lineHeight: 1.5 }

/**
 * The four sub-flag switches this page still owns directly, in the order
 * docs/groups/COMPONENTS-COPY.md's Admin (Refine) copy table lists them.
 * `groups_enabled` (the master) and `groups_browse_enabled` moved to the
 * "Who can use Groups" card above (GroupsAudienceCard, spec §2a/§2b) —
 * `admin_set_groups_access` now owns both rows atomically, and
 * `groups_browse_enabled` is server-owned (left on; see its updated
 * description). Both stay `groups_`-prefixed, so All Flags still hides them
 * (isGroupsOwned) and reaches them only by direct URL.
 */
export const GROUPS_FLAG_KEYS = [
  'groups_class_chapters_enabled',
  'groups_member_create',
  'groups_member_create_requires_review',
  'groups_video_enabled',
] as const

export const isGroupsOwned = (key: string): boolean => key.startsWith('groups_')

/**
 * Catalog: label + help text copied VERBATIM from docs/groups/COMPONENTS-COPY.md
 * "Admin (Refine) copy" → Groups settings card. Do not paraphrase these — mobile
 * QA and the copy doc both assert against this exact wording.
 */
interface CatalogEntry {
  key: (typeof GROUPS_FLAG_KEYS)[number]
  title: string
  help: string
  icon: React.ComponentType<{ size?: number; color?: string }>
  /** Turning this ON has a server-side side effect worth confirming first. */
  confirmOn?: { title: string; content: string; showClassrooms?: boolean }
}

const CATALOG: CatalogEntry[] = [
  {
    key: 'groups_class_chapters_enabled',
    title: 'Class chapters',
    help:
      'Needed for Classrooms. Creates a “Class of YYYY” Classroom for every class year and joins members to theirs. Turning it off closes every Classroom, admins included, but keeps their data.',
    icon: GraduationCap,
    confirmOn: {
      title: 'Turn on Class chapters?',
      content: 'Create chapters for every class year and add members to theirs?',
      showClassrooms: true,
    },
  },
  {
    key: 'groups_member_create',
    title: 'Members can create groups',
    help: 'Lets people who can use Groups (see above) start groups. Admins can always create them.',
    icon: Rocket,
  },
  {
    key: 'groups_member_create_requires_review',
    title: 'Review new member groups',
    help: 'On: member groups wait in Proposals for approval. Off: they go live right away, after automatic text and image checks.',
    icon: ShieldCheck,
  },
  {
    key: 'groups_video_enabled',
    title: 'Video meetups',
    help: 'Allows video and hybrid meetups and huddles in groups and Classrooms. Turning it off stops new calls for everyone, admins included.',
    icon: Bell,
  },
]

/**
 * Mirrors `_classroom_visible_to()` (20261018120000_groups_audience_core.sql:255-269):
 * a Classroom is visible to ANYONE — admins included — only once the RAW
 * `groups_enabled` AND RAW `groups_class_chapters_enabled` flags are both on;
 * either one off closes every Classroom to everyone, with no admin bypass.
 * Only past that gate does an admin short-circuit the rest of the chain
 * (`classroom_settings.mode`, `classroom_open()`, the tester check) — this
 * phrase describes what a MEMBER sees past that same gate, which is what an
 * admin deciding whether to flip a switch needs to know. First match wins.
 *
 * NOT an exact mirror: `_classroom_visible_to()` also lets an active Groups
 * tester see their OWN class year whenever "Who can use Groups"
 * (`groups_rollout.mode`, owned by GroupsAudienceCard) is Admins + testers or
 * Everyone — this function doesn't read that value, so the Admins-mode and
 * Pilot phrases below name the condition instead of asserting it as fact.
 */
export interface ClassroomsStatusInput {
  flagsReadable: boolean
  masterOn: boolean
  chaptersOn: boolean
  settings: ClassroomSettings | null
}

export function classroomsStatusPhrase(i: ClassroomsStatusInput): string {
  if (!i.flagsReadable || !i.settings) return 'can’t tell right now.'
  if (!i.masterOn) return 'closed to everyone, because Groups is Off.'
  if (!i.chaptersOn) return 'closed to everyone, because Class chapters is off.'
  switch (i.settings.mode) {
    case 'off':
      return 'closed to members, because the Classrooms tab is set to Off.'
    case 'admins':
      return 'open to admins, and to Groups testers for their own class while Groups is set to Admins + testers or Everyone. Members see none.'
    case 'pilot': {
      const years = normalizeYears(i.settings.pilot_years)
      return years.length
        ? `open to members of ${classesPhrase(i.settings.pilot_years)}, plus Groups testers for their own class while Groups is set to Admins + testers or Everyone.`
        : 'closed to members, because the pilot has no class years yet, though Groups testers can still see their own class while Groups is set to Admins + testers or Everyone.'
    }
    case 'everyone':
      return 'open to every class.'
    default:
      return 'can’t tell right now.'
  }
}

/**
 * Groups settings — four sub-flags (feature_flags rows `groups_*`), one page
 * per docs/groups/COMPONENTS-COPY.md. Sub-flags are server-composed with the
 * master (public.groups_flag(), migration 20261018120000_groups_audience_core.sql):
 * flipping THIS page's switch for a sub-flag while Groups is off changes the
 * row but has zero effect until Groups is back on, so those rows carry the
 * "Has no effect while Groups is off." hint instead of being disabled
 * outright — an admin preparing chapters ahead of launch still needs to
 * toggle them (see groups_raw_flag(), migration 20260930120000_groups_flags.sql;
 * this page does not call backfill_class_chapters() itself — the database
 * trigger feature_flags_groups_chapters_backfill (migration
 * 20260930120600) does that on the row write).
 *
 * UNREADABLE IS NOT OFF (audit GS-1/F1): the `useList` call passes
 * `queryOptions: { retry: false }` because this page draws its OWN "Try
 * again" (mirroring jaguar_owned_controls/page.tsx) rather than fighting a
 * silent background retry. A failed read renders a dedicated "Unreadable"
 * block — no switch is drawn, because a disabled unchecked switch still
 * *reads* as Off, which is exactly the guess this page must not make. Both
 * stat tiles fall back to "—" for the same reason. `loaded`/the "Try again"
 * gate below key off `query.isPending`, not `query.isLoading`: a first fetch
 * paused by the browser going offline reports `isLoading: false` in
 * TanStack Query 5 (see CLAUDE.md's TanStack traps) while `status` is still
 * "pending", so `isLoading` alone would have drawn the exact same false
 * "Off" this page exists to avoid. `retrying` keeps the Unreadable block (and
 * its own spinner) mounted through a Try-again click, because re-fetching an
 * errored query also resets `status` to "pending" for that one window —
 * without it, clicking Try again would flash "Loading…" over the block that
 * should show its own progress (mirrors jaguar_owned_controls/page.tsx). A
 * row that read fine but has no database row yet (`missing`) is the one case
 * that IS drawn as Off: the server itself reads a missing row as off
 * (groups_flag()'s COALESCE), so the switch is unchecked and disabled rather
 * than hidden.
 *
 * Groups browsing (groups_browse_enabled) is server-owned and left ON: it now
 * means "show browse surfaces to whoever GroupsAudienceCard's server audience
 * lets in", not "show them to every member" — the audience is what actually
 * gates visibility (see the row's own description). The app still ANDs it
 * with the per-caller `groups_enabled` in useGroupsFlags() to hide the Groups
 * tab, Home card, My Groups and create/ideas from anyone outside the
 * audience.
 *
 * The Classrooms footer line (audit GS-5) answers a question this page would
 * otherwise leave the admin to work out by hand: whether Class chapters
 * being on/off, Groups being on/off AND the separate Classrooms tab
 * (classroom_settings.mode, decided at `/group_chapters`) together leave any
 * Classroom actually open to members right now. `classroomsStatusPhrase()`
 * mirrors the server's own gate for the master/chapters/mode chain, so those
 * three phrases can never say more than the database would allow — but it
 * does NOT read `groups_rollout.mode` (the separate "Who can use Groups"
 * audience), so the Admins-mode and Pilot phrases NAME that condition
 * ("while Groups is set to Admins + testers or Everyone") instead of
 * asserting it, rather than repeat the audit's GS-5 finding that this phrase
 * once claimed testers could see a Classroom it hadn't verified they could.
 * Turning Class chapters ON is confirmed first — the
 * flip itself runs a database trigger
 * (feature_flags_groups_chapters_backfill, migration 20260930120600) that
 * creates every class-year chapter and backfills membership — and that
 * confirm also previews the Classrooms line, since flipping this one switch
 * can reopen a pilot or everyone-mode Classroom immediately.
 *
 * "Applies on the server at once; apps catch up on their next refresh" is the
 * truthful wording (an earlier, faster-sounding claim was wrong): Realtime on
 * `feature_flags`/`classroom_settings` delivers only the ROW that changed,
 * and row visibility is per caller
 * (groups_flag_row_visible / groups_flag()), so a member's already-running
 * app only sees a widened or narrowed switch on its next full flags load,
 * never mid-session (docs/classrooms/ROLLOUT.md:197-202).
 *
 * GroupsAudienceCard's own `useGroupsAudience` hook invalidates this page's
 * `feature_flags` list (SA-03, fix round 1) right after a successful
 * admin_set_groups_access — Refine's list query is cached (staleTime 5 min,
 * App.tsx:937-942) and is a completely separate observer from the card's own
 * raw-client read, so without that invalidate the switches below and their
 * "Has no effect while Groups is off." hints would keep showing the master
 * as Off for up to five minutes after the card turned it on.
 */
export function GroupsSettingsPage() {
  const { message, modal } = App.useApp()
  const { result, query } = useList<FeatureFlag>({
    resource: 'feature_flags',
    pagination: { pageSize: 200 },
    // This page has its own "Try again" below, as jaguar_owned_controls does —
    // a silent background retry would fight it (see the module doc above).
    queryOptions: { retry: false },
  })
  const { mutate: update } = useUpdate()
  const classroom = useClassroomSettings()

  // Keeps the Unreadable block (and its own spinner) mounted through a Try
  // again click — see the module doc above.
  const [retrying, setRetrying] = React.useState(false)
  async function handleRetry() {
    setRetrying(true)
    try {
      await query.refetch()
    } finally {
      setRetrying(false)
    }
  }

  const rows = result?.data ?? []
  const byKey = new Map(rows.map((r) => [r.key, r]))
  const unreadable = query.isError || retrying
  // isPending (not isLoading): a first fetch paused offline is isLoading:false
  // but still status "pending" — see the module doc above.
  const loaded = !query.isPending && !unreadable
  const masterOn = byKey.get('groups_enabled')?.enabled === true
  const chaptersOn = byKey.get('groups_class_chapters_enabled')?.enabled === true

  const classroomsPhrase = classroomsStatusPhrase({
    flagsReadable: !unreadable,
    masterOn,
    chaptersOn,
    settings: classroom.unreadable ? null : classroom.settings,
  })

  function applyToggle(entry: CatalogEntry, checked: boolean) {
    update({ resource: 'feature_flags', id: entry.key, values: { enabled: checked }, meta: { idColumnName: 'key' } })
  }

  function onToggle(entry: CatalogEntry, checked: boolean) {
    if (checked && entry.confirmOn) {
      modal.confirm({
        title: entry.confirmOn.title,
        content: (
          <>
            <div>{entry.confirmOn.content}</div>
            {entry.confirmOn.showClassrooms && (
              <div data-testid="groups-settings-chapters-confirm-classrooms" style={{ marginTop: 8 }}>
                Classrooms after this:{' '}
                {classroomsStatusPhrase({
                  flagsReadable: true,
                  masterOn,
                  chaptersOn: true,
                  settings: classroom.unreadable ? null : classroom.settings,
                })}
              </div>
            )}
          </>
        ),
        okText: 'Turn on',
        cancelText: 'Cancel',
        onOk: () => applyToggle(entry, true),
      })
      return
    }
    applyToggle(entry, checked)
  }

  const stats: Stat[] = loaded
    ? [
        { label: 'Groups', value: masterOn ? 'On' : 'Off', icon: UsersRound, tone: masterOn ? 'green' : 'neutral' },
        { label: 'Class chapters', value: chaptersOn ? 'On' : 'Off', icon: GraduationCap, tone: chaptersOn ? 'green' : 'neutral' },
      ]
    : [
        { label: 'Groups', value: '—', icon: UsersRound, tone: 'neutral' },
        { label: 'Class chapters', value: '—', icon: GraduationCap, tone: 'neutral' },
      ]

  return (
    <ListPage
      intro={{ eyebrow: 'Community · Groups', description: 'Applies on the server at once; apps catch up on their next refresh' }}
      stats={stats}
      statColumns={2}
    >
      <GroupsAudienceCard />

      <SectionCard title="Groups settings" sub="Applies on the server at once; apps catch up on their next refresh" style={{ maxWidth: 720 }}>
        {unreadable ? (
          <div data-testid="groups-settings-unreadable" style={{ padding: '16px 0' }}>
            <div style={{ marginBottom: 6 }}>
              <Pill tone="red">Unreadable</Pill>
            </div>
            <div style={muted}>Couldn’t read these switches, so this page won’t guess. Try again before changing anything.</div>
            <Button
              size="small"
              data-testid="groups-settings-retry"
              style={{ marginTop: 10 }}
              loading={query.isFetching}
              onClick={() => void handleRetry()}
            >
              Try again
            </Button>
          </div>
        ) : query.isPending ? (
          <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 13, color: brand.sub, padding: '24px 0' }}>
            Loading…
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {CATALOG.map((entry, idx) => {
              const row = byKey.get(entry.key) ?? null
              const missing = row === null
              const enabled = row?.enabled === true
              const Icon = entry.icon
              const dependentAndOff = !masterOn
              return (
                <div
                  key={entry.key}
                  data-testid={`groups-settings-row-${entry.key}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                    padding: '16px 0',
                    borderBottom: idx < CATALOG.length - 1 ? `1px solid ${brand.cardLine}` : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1 }}>
                    <div style={{ marginTop: 2, flexShrink: 0 }}>
                      <Icon size={18} color={brand.jaguar} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontFamily: brand.fSans,
                          fontWeight: 600,
                          fontSize: 13,
                          color: brand.ink,
                          marginBottom: 3,
                        }}
                      >
                        {entry.title}
                      </div>
                      <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 12, color: brand.sub, lineHeight: 1.5 }}>
                        {entry.help}
                      </div>
                      {dependentAndOff && (
                        <div
                          data-testid={`groups-settings-hint-${entry.key}`}
                          style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 11, color: brand.sub, marginTop: 5, fontStyle: 'italic' }}
                        >
                          Has no effect while Groups is off.
                        </div>
                      )}
                      {missing && (
                        <div
                          data-testid={`groups-settings-missing-${entry.key}`}
                          style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 11, color: brand.sub, marginTop: 5, fontStyle: 'italic' }}
                        >
                          Not in the database yet, so it stays off.
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    <Tooltip title={enabled ? `Disable "${entry.title}"` : `Enable "${entry.title}"`}>
                      <Switch
                        data-testid={`groups-settings-switch-${entry.key}`}
                        checked={enabled}
                        disabled={missing}
                        onChange={(checked) => onToggle(entry, checked)}
                      />
                    </Tooltip>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        <div
          data-testid="groups-settings-classrooms"
          style={{ ...muted, borderTop: `1px solid ${brand.cardLine}`, paddingTop: 12, marginTop: 4 }}
        >
          {!query.isPending && !classroom.loading && (
            <span data-testid="groups-settings-classrooms-status">Classrooms: {classroomsPhrase}</span>
          )}{' '}
          <Link to="/group_chapters">Open the Classrooms tab</Link>
        </div>
      </SectionCard>
    </ListPage>
  )
}
